import { Alert, Button, ButtonProps, Stack, Typography } from '@mui/material';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import * as Sentry from '@sentry/capacitor';
import { useNotificationsSettings } from 'features/notifications/hooks/useNotificationsSettings';
import { defer } from 'lodash-es';
import { Capacitor } from '@capacitor/core';
import { EnableNotificationsDialog } from './EnableNotificationsDialog';
import { useWildfireNotifications } from './useWildfireNotifications';

type WildfireNotificationsButtonProps = {
  wildfire: Wildfire;
};

type NotificationsEnabledButtonProps = WildfireNotificationsButtonProps;

const getCommonButtonProps = (): ButtonProps => ({
  fullWidth: true,
  color: 'inherit',
  variant: 'outlined',
  size: 'large',
  sx: {
    fontSize: 12,
    textTransform: 'uppercase',
    fontWeight: 'fontWeightBold',
  },
});

const NotificationsDisabledButton = (): JSX.Element => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <>
      <Button
        {...getCommonButtonProps()}
        startIcon={<NotificationsActiveIcon sx={{ width: 16, height: 16 }} />}
        onClick={() => setDialogOpen(true)}
        data-testid="notifications-disabled-button"
      >
        {t('geoEvent.notifications.on')}
      </Button>

      <EnableNotificationsDialog open={dialogOpen} onSetOpen={setDialogOpen} />
    </>
  );
};

const NotificationsEnabledButton = (
  props: NotificationsEnabledButtonProps,
): JSX.Element => {
  const { wildfire } = props;
  const { t } = useTranslation();
  const {
    isSubscribedToNotifications,
    showSubscribeToSilentNotifications,
    toggleNotificationsSubscription,
    subscribeToSilentNotifications,
  } = useWildfireNotifications(wildfire);
  const audioRef = useRef<HTMLAudioElement>(null);

  const handleAudioEnded = (): void => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    // Clear the audio source to force iOS to terminate the media session.
    // This helps remove the media controls from the notification center
    // after playback completes.
    audio.src = '';

    // Defer restoring the audio source until after the current call stack clears.
    // This ensures the media session has fully terminated before we prepare
    // the audio element for future playback.
    defer(() => {
      audio.src = '/notification.wav';
    });

    // Explicitly tell the Media Session API that playback has stopped,
    // which helps iOS understand that it should remove the media controls.
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = 'none';
    }
  };

  const handleClick = (): void => {
    toggleNotificationsSubscription();
    const audio = audioRef.current;
    if (!isSubscribedToNotifications && audio) {
      try {
        // Reset position to start from beginning if interrupted
        audio.currentTime = 0;
        audio.play();
      } catch (err) {
        Sentry.captureException(err);
      }
    }
  };

  return (
    <Stack spacing={2}>
      <Button
        {...getCommonButtonProps()}
        startIcon={
          isSubscribedToNotifications ? (
            <NotificationsOffIcon sx={{ width: 16, height: 16 }} />
          ) : (
            <NotificationsActiveIcon sx={{ width: 16, height: 16 }} />
          )
        }
        onClick={handleClick}
        data-testid="notifications-enabled-button"
      >
        {isSubscribedToNotifications
          ? t('geoEvent.notifications.off')
          : t('geoEvent.notifications.on')}
      </Button>

      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        src="/notification.wav"
        preload="auto"
        hidden
        onEnded={
          Capacitor.getPlatform() === 'ios' ? handleAudioEnded : undefined
        }
        data-testid="hidden-audio-notification"
      />

      {showSubscribeToSilentNotifications && (
        <Alert
          severity="warning"
          icon={false}
          slotProps={{ message: { sx: { width: '100%' } } }}
        >
          <Stack spacing={1}>
            <Typography>
              {isSubscribedToNotifications
                ? t('geoEvent.notifications.silent.on')
                : t('geoEvent.notifications.silent.off')}
            </Typography>

            <Button
              onClick={subscribeToSilentNotifications}
              variant="watchduty-text-uppercase"
              sx={{ color: 'text.primary', width: 'fit-content' }}
            >
              {t('geoEvent.notifications.enableNotifications')}
            </Button>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
};

export const WildfireNotificationsButton = (
  props: WildfireNotificationsButtonProps,
): JSX.Element => {
  const { wildfire } = props;
  const { isPushEnabled } = useNotificationsSettings({
    regionSettingsQueryEnabled: false,
  });

  if (!isPushEnabled) {
    return <NotificationsDisabledButton />;
  }

  return <NotificationsEnabledButton wildfire={wildfire} />;
};
